<script setup>
/**
 * 登录 / 注册 / 忘记密码弹窗。小镇默认公开可看；只有接入自己的 Agent、发指令时才需要登录。
 * 路由到 /login（可带 ?next=）即打开本弹窗，关闭后回到小镇。
 * 提交前先在页面上查格式；服务端的失败按错误码换成一句能照着做的话（错误码见 packages/auth 与 routes/auth.js）。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Input, Modal } from '@drama/ui'

import { api, setSession } from './api.js'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const open = computed(() => route.path === '/login')
/** 服务端有验证码邮件模板的语言（apps/voko-server 的 EMAIL_LOCALES）；网页语言没有对应模板（如 id）时邮件用英文。 */
const EMAIL_LOCALES = new Set(['en', 'zh', 'ja', 'ko', 'es', 'pt'])
const emailLocale = computed(() => (EMAIL_LOCALES.has(locale.value) ? locale.value : 'en'))
// 与 @voko/access 的 HANDLE_PATTERN 相同（那个包是服务端的，网页不引）。
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_-]{1,31}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// 与服务端验证码发送窗口（packages/auth 的 DEFAULT_CODE_SEND_WINDOW_MS）一样长。
const SEND_COOLDOWN_SEC = 60

/** @type {import('vue').Ref<'login'|'register'|'reset'>} */
const mode = ref('login')
// 顶栏的「注册」带 ?mode=register 打开，直接是注册表单。
watch(() => route.query.mode, (value) => { mode.value = value === 'register' ? 'register' : 'login' }, { immediate: true })
const email = ref('')
const password = ref('')
const code = ref('')
const handle = ref('')
/** @type {import('vue').Ref<{ kind: 'error'|'info', text: string }|null>} */
const message = ref(null)
const busy = ref(false)
const cooldown = ref(0)
let timer = 0

const TITLES = { login: 'login.title', register: 'login.registerTitle', reset: 'login.resetTitle' }
const SUBMITS = { login: 'login.submit', register: 'login.register', reset: 'login.resetSubmit' }
const NEEDS = { login: ['password'], register: ['password', 'handle', 'code'], reset: ['password', 'code'] }

/** @param {'login'|'register'|'reset'} next */
function switchMode(next) {
  mode.value = next
  message.value = null
  code.value = ''
}

/** @param {number} sec */
function startCooldown(sec) {
  cooldown.value = sec
  clearInterval(timer)
  timer = setInterval(() => {
    cooldown.value -= 1
    if (cooldown.value <= 0) clearInterval(timer)
  }, 1000)
}
onBeforeUnmount(() => clearInterval(timer))

/**
 * 页面上能查的先查：省一次请求，也免得把服务端的英文校验信息给人看。
 * @param {string[]} needs 除邮箱外要查的项
 * @returns {string|null} 第一条问题的文案 key，没有问题返回 null
 */
function formProblem(needs) {
  if (!EMAIL_PATTERN.test(email.value.trim())) return 'login.err.emailFormat'
  if (needs.includes('password')) {
    if (password.value === '') return 'login.err.passwordEmpty'
    if (mode.value !== 'login' && password.value.length < 8) return 'login.err.passwordShort'
  }
  if (needs.includes('handle') && !HANDLE_PATTERN.test(handle.value.trim().toLowerCase())) return 'login.err.handleFormat'
  if (needs.includes('code') && !/^\d{6}$/.test(code.value.trim())) return 'login.err.codeFormat'
  return null
}

/**
 * 把一次失败换成给人看的话。
 * @param {any} err api() 抛出的错误（带服务端错误码 code 与 details）；fetch 的网络错误没有 code
 * @param {'login'|'send'|'submit'} action
 */
function errorText(err, action) {
  if (err.code === undefined) return t('login.err.network')
  switch (err.code) {
    case 'USER_NOT_FOUND': return t('login.err.userNotFound')
    case 'INVALID_CREDENTIALS': return t('login.err.wrongPassword', { left: err.details.attempts_left })
    case 'TOO_MANY_ATTEMPTS':
      return action === 'send'
        ? t('login.err.tooManySends', { sec: err.details.retry_after_sec })
        : t('login.err.tooManyLogins', { min: Math.ceil(err.details.retry_after_sec / 60) })
    case 'EMAIL_TAKEN': return t('login.err.emailTaken')
    case 'CONFLICT': return t('login.err.handleTaken')
    case 'CODE_INVALID': return t('login.err.codeInvalid')
    case 'CODE_EXPIRED': return t('login.err.codeExpired')
    case 'validation_error': return t('login.err.invalidInput')
    // 其余（服务端 500 等）不是用户能改的，给一句通用的话并带上错误码，方便报告。
    default: return t('login.err.unknown', { code: err.code })
  }
}

async function sendCode() {
  const problem = formProblem([])
  if (problem !== null) { message.value = { kind: 'error', text: t(problem) }; return }
  busy.value = true
  try {
    const path = mode.value === 'reset' ? '/api/voko/auth/password-reset/send-code' : '/api/voko/auth/send-code'
    await api('POST', path, { email: email.value.trim(), locale: emailLocale.value })
    message.value = { kind: 'info', text: t('login.codeSent') }
    startCooldown(SEND_COOLDOWN_SEC)
  } catch (err) {
    message.value = { kind: 'error', text: errorText(err, 'send') }
    if (err.code === 'TOO_MANY_ATTEMPTS') startCooldown(err.details.retry_after_sec)
  } finally {
    busy.value = false
  }
}

async function submit() {
  const problem = formProblem(NEEDS[mode.value])
  if (problem !== null) { message.value = { kind: 'error', text: t(problem) }; return }
  busy.value = true
  try {
    const body = { email: email.value.trim(), password: password.value }
    switch (mode.value) {
      case 'login':
        setSession(await api('POST', '/api/voko/auth/login', { method: 'password', ...body }))
        break
      case 'register':
        setSession(await api('POST', '/api/voko/auth/register', { ...body, verification_code: code.value.trim(), locale: emailLocale.value, handle: handle.value.trim().toLowerCase() }))
        break
      case 'reset':
        await api('POST', '/api/voko/auth/password-reset', { ...body, verification_code: code.value.trim() })
        switchMode('login')
        password.value = ''
        message.value = { kind: 'info', text: t('login.resetDone') }
        return
      default:
        throw new Error(`Unhandled login mode: ${mode.value}`)
    }
    password.value = ''
    message.value = null
    // 只接受站内路径（如登录后回到原来要看的页面）。
    const next = typeof route.query.next === 'string' && route.query.next.startsWith('/') && !route.query.next.startsWith('//') ? route.query.next : '/agents'
    await router.push(next)
  } catch (err) {
    message.value = { kind: 'error', text: errorText(err, mode.value === 'login' ? 'login' : 'submit') }
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal :open="open" :title="t(TITLES[mode])" width="420px" @close="router.push('/')">
    <!-- 包一层 form：回车即提交。novalidate：格式由 formProblem 查，提示走同一处。 -->
    <form class="voko-login" novalidate @submit.prevent="submit">
      <p>{{ mode === 'reset' ? t('login.resetWhy') : t('login.why') }}</p>
      <label class="voko-field">{{ t('login.email') }}<Input v-model="email" type="email" autocomplete="email" /></label>
      <label class="voko-field">{{ mode === 'reset' ? t('login.newPassword') : t('login.password') }}<Input v-model="password" type="password" :autocomplete="mode === 'login' ? 'current-password' : 'new-password'" /></label>
      <p v-if="mode !== 'login'" class="voko-login__hint">{{ t('login.passwordRule') }}</p>
      <label v-if="mode === 'register'" class="voko-field">{{ t('login.handle') }}<Input v-model="handle" autocomplete="username" /></label>
      <div v-if="mode !== 'login'" class="voko-login__code">
        <label class="voko-field">{{ t('login.code') }}<Input v-model="code" inputmode="numeric" autocomplete="one-time-code" /></label>
        <Button variant="secondary" :disabled="busy || cooldown > 0" @click="sendCode">{{ cooldown > 0 ? t('login.resendIn', { sec: cooldown }) : t('login.sendCode') }}</Button>
      </div>
      <Button v-if="mode === 'login'" variant="ghost" size="sm" @click="switchMode('reset')">{{ t('login.forgot') }}</Button>
      <p v-if="message" :role="message.kind === 'error' ? 'alert' : 'status'" :class="{ 'voko-login__error': message.kind === 'error' }">{{ message.text }}</p>
      <button type="submit" hidden />
    </form>
    <template #footer>
      <Button v-if="mode === 'reset'" variant="ghost" @click="switchMode('login')">{{ t('login.backToLogin') }}</Button>
      <Button v-else variant="ghost" @click="switchMode(mode === 'login' ? 'register' : 'login')">{{ mode === 'login' ? t('login.toRegister') : t('login.toLogin') }}</Button>
      <Button :loading="busy" @click="submit">{{ t(SUBMITS[mode]) }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.voko-login__code { display: flex; align-items: flex-end; gap: var(--drama-space-sm); margin-bottom: var(--drama-space-md); }
.voko-login__code .voko-field { flex: 1; margin-bottom: 0; }
.voko-login__hint { margin: calc(-1 * var(--drama-space-sm)) 0 var(--drama-space-md); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.voko-login__error { color: var(--drama-color-danger); }
</style>
